import React, { useState } from 'react';
import { Search, Filter, Calendar, Users, Target, DollarSign, BarChart3, Settings, ChevronDown, ChevronUp, Globe, Footprints, Shield, FileText, UserCheck, TrendingUp } from 'lucide-react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

interface ModernFiltersPanelProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  filter: 'all' | 'this_season' | 'one_year' | 'long_term';
  setFilter: (value: 'all' | 'this_season' | 'one_year' | 'long_term') => void;
  positionFilter: string;
  setPositionFilter: (value: string) => void;
  leagueFilter: string;
  setLeagueFilter: (value: string) => void;
  ageRange: [number, number];
  setAgeRange: (value: [number, number]) => void;
  heightRange: [number, number];
  setHeightRange: (value: [number, number]) => void;
  marketValueFilterType: 'all' | 'low' | 'high';
  setMarketValueFilterType: (value: 'all' | 'low' | 'high') => void;
  marketValueRangeAll: [number, number];
  setMarketValueRangeAll: (value: [number, number]) => void;
  marketValueRangeLow: [number, number];
  setMarketValueRangeLow: (value: [number, number]) => void;
  marketValueRangeHigh: [number, number];
  setMarketValueRangeHigh: (value: [number, number]) => void;
  hasReportsFilter: boolean;
  setHasReportsFilter: (value: boolean) => void;
  availablePositions: string[];
  availableLeagues: string[];
  setCurrentPage: (page: number) => void;
  onLoadData: () => void;
  // New filter props
  citizenshipFilter?: string;
  setCitizenshipFilter?: (value: string) => void;
  footFilter?: string;
  setFootFilter?: (value: string) => void;
  isEuFilter?: boolean | null;
  setIsEuFilter?: (value: boolean | null) => void;
  countryOfBirthFilter?: string;
  setCountryOfBirthFilter?: (value: string) => void;
  currentClubFilter?: string;
  setCurrentClubFilter?: (value: string) => void;
  contractExpiresFrom?: string;
  setContractExpiresFrom?: (value: string) => void;
  contractExpiresTo?: string;
  setContractExpiresTo?: (value: string) => void;
  playerAgentFilter?: string;
  setPlayerAgentFilter?: (value: string) => void;
  minGoals?: number;
  setMinGoals?: (value: number) => void;
  minAssists?: number;
  setMinAssists?: (value: number) => void;
  minMatchesPlayed?: number;
  setMinMatchesPlayed?: (value: number) => void;
}

const ModernFiltersPanel: React.FC<ModernFiltersPanelProps> = ({
  searchTerm,
  setSearchTerm,
  filter,
  setFilter,
  positionFilter,
  setPositionFilter,
  leagueFilter,
  setLeagueFilter,
  ageRange,
  setAgeRange,
  heightRange,
  setHeightRange,
  marketValueFilterType,
  setMarketValueFilterType,
  marketValueRangeAll,
  setMarketValueRangeAll,
  marketValueRangeLow,
  setMarketValueRangeLow,
  marketValueRangeHigh,
  setMarketValueRangeHigh,
  availablePositions,
  availableLeagues,
  setCurrentPage,
  onLoadData,
  // New filters with default values
  citizenshipFilter = '',
  setCitizenshipFilter = () => {},
  footFilter = 'all',
  setFootFilter = () => {},
  isEuFilter = null,
  setIsEuFilter = () => {},
  countryOfBirthFilter = '',
  setCountryOfBirthFilter = () => {},
  currentClubFilter = '',
  setCurrentClubFilter = () => {},
  contractExpiresFrom = '',
  setContractExpiresFrom = () => {},
  contractExpiresTo = '',
  setContractExpiresTo = () => {},
  playerAgentFilter = '',
  setPlayerAgentFilter = () => {},
  minGoals = 0,
  setMinGoals = () => {},
  minAssists = 0,
  setMinAssists = () => {},
  minMatchesPlayed = 0,
  setMinMatchesPlayed = () => {}
}) => {
  // State for showing/hiding advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showStatsFilters, setShowStatsFilters] = useState(false);

  const periodOptions = [
    { value: 'all', label: 'Todos los períodos', icon: Calendar, color: 'gray' },
    { value: 'this_season', label: 'Esta temporada', icon: Target, color: 'red' },
    { value: 'one_year', label: 'Próximo año', icon: BarChart3, color: 'orange' },
    { value: 'long_term', label: 'Larga duración', icon: Settings, color: 'green' }
  ];

  const marketValueOptions = [
    { value: 'low', label: 'Oportunidades (€0-10M)', color: 'green' },
    { value: 'high', label: 'Estrellas (€10M+)', color: 'purple' },
    { value: 'all', label: 'Todos los valores', color: 'blue' }
  ];

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
            <Filter className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white text-lg font-bold">Filtros Avanzados</h3>
            <p className="text-blue-100 text-sm">Personaliza tu búsqueda de contratos</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur"></div>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar jugador, equipo, liga..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                onKeyDown={(e) => e.key === 'Enter' && onLoadData()}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-500"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contract Period Filters */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-blue-600" />
              <h4 className="font-semibold text-gray-800">Período de Contrato</h4>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {periodOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = filter === option.value;
                
                // Define colors explicitly for Tailwind to compile
                const colorClasses = {
                  gray: {
                    border: 'border-gray-500',
                    bg: 'bg-gray-50',
                    iconBg: 'bg-gray-500',
                    text: 'text-gray-700'
                  },
                  red: {
                    border: 'border-red-500',
                    bg: 'bg-red-50',
                    iconBg: 'bg-red-500',
                    text: 'text-red-700'
                  },
                  orange: {
                    border: 'border-orange-500',
                    bg: 'bg-orange-50',
                    iconBg: 'bg-orange-500',
                    text: 'text-orange-700'
                  },
                  green: {
                    border: 'border-green-500',
                    bg: 'bg-green-50',
                    iconBg: 'bg-green-500',
                    text: 'text-green-700'
                  }
                };
                
                const colors = colorClasses[option.color as keyof typeof colorClasses];
                
                return (
                  <button
                    key={option.value}
                    onClick={() => {
                      setFilter(option.value as any);
                      setCurrentPage(1);
                    }}
                    className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                      isSelected
                        ? `${colors.border} ${colors.bg} shadow-lg`
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isSelected ? colors.iconBg : 'bg-gray-400'
                      }`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <span className={`text-sm font-medium ${
                        isSelected ? colors.text : 'text-gray-600'
                      }`}>
                        {option.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Position and League Filters */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Posición</span>
              </div>
              <select
                value={positionFilter}
                onChange={(e) => {
                  setPositionFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 bg-gray-50 focus:bg-white"
              >
                <option value="all">Todas las posiciones</option>
                {availablePositions.map(position => (
                  <option key={position} value={position}>{position}</option>
                ))}
              </select>

              <select
                value={leagueFilter}
                onChange={(e) => {
                  setLeagueFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 bg-gray-50 focus:bg-white"
              >
                <option value="all">Todas las ligas</option>
                {availableLeagues.map(league => (
                  <option key={league} value={league}>{league}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Age and Height Ranges */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              <h4 className="font-semibold text-gray-800">Características Físicas</h4>
            </div>

            <div className="space-y-6">
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <label className="block text-sm font-semibold text-purple-700 mb-3">
                  Edad: {ageRange[0]} - {ageRange[1]} años
                </label>
                <Slider
                  range
                  min={15}
                  max={40}
                  step={1}
                  value={ageRange}
                  onChange={(value) => {
                    setAgeRange(value as [number, number]);
                    setCurrentPage(1);
                  }}
                  trackStyle={[{ backgroundColor: '#8b5cf6' }]}
                  handleStyle={[
                    { borderColor: '#8b5cf6', backgroundColor: '#8b5cf6' },
                    { borderColor: '#8b5cf6', backgroundColor: '#8b5cf6' }
                  ]}
                />
              </div>

              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200">
                <label className="block text-sm font-semibold text-indigo-700 mb-3">
                  Altura: {heightRange[0]}m - {heightRange[1]}m
                </label>
                <Slider
                  range
                  min={1.50}
                  max={2.10}
                  step={0.01}
                  value={heightRange}
                  onChange={(value) => {
                    setHeightRange(value as [number, number]);
                    setCurrentPage(1);
                  }}
                  trackStyle={[{ backgroundColor: '#6366f1' }]}
                  handleStyle={[
                    { borderColor: '#6366f1', backgroundColor: '#6366f1' },
                    { borderColor: '#6366f1', backgroundColor: '#6366f1' }
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Market Value Filters */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-gray-800">Valor de Mercado</h4>
            </div>

            <div className="space-y-3">
              {marketValueOptions.map((option) => {
                // Define colors explicitly for Tailwind to compile
                const colorClasses = {
                  green: {
                    radio: 'text-green-600',
                    focus: 'focus:ring-green-500',
                    text: 'text-green-700'
                  },
                  purple: {
                    radio: 'text-purple-600',
                    focus: 'focus:ring-purple-500',
                    text: 'text-purple-700'
                  },
                  blue: {
                    radio: 'text-blue-600',
                    focus: 'focus:ring-blue-500',
                    text: 'text-blue-700'
                  }
                };
                
                const colors = colorClasses[option.color as keyof typeof colorClasses];
                
                return (
                  <label key={option.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="marketValueFilterType"
                      value={option.value}
                      checked={marketValueFilterType === option.value}
                      onChange={() => {
                        setMarketValueFilterType(option.value as any);
                        setCurrentPage(1);
                      }}
                      className={`w-4 h-4 ${colors.radio} border-gray-300 ${colors.focus}`}
                    />
                    <span className={`ml-3 text-sm font-medium ${colors.text}`}>
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>

            {/* Range Sliders */}
            {marketValueFilterType === 'low' && (
              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <p className="text-sm font-semibold text-green-700 mb-3">
                  €{marketValueRangeLow[0].toFixed(2)}M - €{marketValueRangeLow[1].toFixed(2)}M
                </p>
                <Slider
                  range
                  min={0}
                  max={10}
                  step={0.25}
                  value={marketValueRangeLow}
                  onChange={(val) => {
                    setMarketValueRangeLow(val as [number, number]);
                    setCurrentPage(1);
                  }}
                  trackStyle={[{ backgroundColor: '#10b981' }]}
                  handleStyle={[
                    { borderColor: '#10b981', backgroundColor: '#10b981' },
                    { borderColor: '#10b981', backgroundColor: '#10b981' }
                  ]}
                />
              </div>
            )}

            {marketValueFilterType === 'high' && (
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <p className="text-sm font-semibold text-purple-700 mb-3">
                  €{marketValueRangeHigh[0].toFixed(1)}M - €{marketValueRangeHigh[1].toFixed(1)}M
                </p>
                <Slider
                  range
                  min={10}
                  max={200}
                  step={0.5}
                  value={marketValueRangeHigh}
                  onChange={(val) => {
                    setMarketValueRangeHigh(val as [number, number]);
                    setCurrentPage(1);
                  }}
                  trackStyle={[{ backgroundColor: '#8b5cf6' }]}
                  handleStyle={[
                    { borderColor: '#8b5cf6', backgroundColor: '#8b5cf6' },
                    { borderColor: '#8b5cf6', backgroundColor: '#8b5cf6' }
                  ]}
                />
              </div>
            )}

            {marketValueFilterType === 'all' && (
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">
                  €{marketValueRangeAll[0].toFixed(1)}M - €{marketValueRangeAll[1].toFixed(1)}M
                </p>
                <Slider
                  range
                  min={0}
                  max={200}
                  step={0.5}
                  value={marketValueRangeAll}
                  onChange={(val) => {
                    setMarketValueRangeAll(val as [number, number]);
                    setCurrentPage(1);
                  }}
                  trackStyle={[{ backgroundColor: '#3b82f6' }]}
                  handleStyle={[
                    { borderColor: '#3b82f6', backgroundColor: '#3b82f6' },
                    { borderColor: '#3b82f6', backgroundColor: '#3b82f6' }
                  ]}
                />
              </div>
            )}

            {/* Additional filters - "Solo jugadores con informes" checkbox */}
            
          </div>
        </div>

        {/* Advanced Filters Section */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="flex items-center justify-between w-full px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 rounded-xl transition-all duration-300"
          >
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-indigo-600" />
              <h4 className="font-semibold text-gray-800">Filtros Avanzados</h4>
              <span className="text-xs text-gray-500">
                (Nacionalidad, Pie, Agente, etc.)
              </span>
            </div>
            {showAdvancedFilters ? (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </button>

          {showAdvancedFilters && (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in">
              {/* Nationality & Origin Filters */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5 text-indigo-600" />
                  <h4 className="font-semibold text-gray-800">Nacionalidad y Origen</h4>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nacionalidad
                    </label>
                    <input
                      type="text"
                      placeholder="Ej: España, Argentina, Brasil..."
                      value={citizenshipFilter}
                      onChange={(e) => {
                        setCitizenshipFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all duration-300 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      País de Nacimiento
                    </label>
                    <input
                      type="text"
                      placeholder="Ej: Spain, Argentina..."
                      value={countryOfBirthFilter}
                      onChange={(e) => {
                        setCountryOfBirthFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all duration-300 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  <div className="pt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      <Shield className="w-4 h-4 inline mr-2 text-blue-600" />
                      Ciudadano UE
                    </label>
                    <div className="flex gap-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="isEu"
                          checked={isEuFilter === null}
                          onChange={() => {
                            setIsEuFilter(null);
                            setCurrentPage(1);
                          }}
                          className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Todos</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="isEu"
                          checked={isEuFilter === true}
                          onChange={() => {
                            setIsEuFilter(true);
                            setCurrentPage(1);
                          }}
                          className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Sí</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="isEu"
                          checked={isEuFilter === false}
                          onChange={() => {
                            setIsEuFilter(false);
                            setCurrentPage(1);
                          }}
                          className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">No</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Physical & Technical Attributes */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Footprints className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-gray-800">Atributos Físicos y Técnicos</h4>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pie Dominante
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => {
                          setFootFilter('all');
                          setCurrentPage(1);
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          footFilter === 'all'
                            ? 'bg-purple-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Todos
                      </button>
                      <button
                        onClick={() => {
                          setFootFilter('right');
                          setCurrentPage(1);
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          footFilter === 'right'
                            ? 'bg-purple-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Derecho
                      </button>
                      <button
                        onClick={() => {
                          setFootFilter('left');
                          setCurrentPage(1);
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          footFilter === 'left'
                            ? 'bg-purple-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Zurdo
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Club & Contract Filters */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-gray-800">Club y Contrato</h4>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Club Actual
                    </label>
                    <input
                      type="text"
                      placeholder="Ej: Real Madrid, Barcelona..."
                      value={currentClubFilter}
                      onChange={(e) => {
                        setCurrentClubFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contrato vence desde
                    </label>
                    <input
                      type="date"
                      value={contractExpiresFrom}
                      onChange={(e) => {
                        setContractExpiresFrom(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contrato vence hasta
                    </label>
                    <input
                      type="date"
                      value={contractExpiresTo}
                      onChange={(e) => {
                        setContractExpiresTo(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <UserCheck className="w-4 h-4 inline mr-2 text-green-600" />
                      Agente del Jugador
                    </label>
                    <input
                      type="text"
                      placeholder="Nombre del agente..."
                      value={playerAgentFilter}
                      onChange={(e) => {
                        setPlayerAgentFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300 bg-gray-50 focus:bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Performance Stats Filters Section */}
        <div className="mt-6 border-t border-gray-200 pt-6">
          <button
            onClick={() => setShowStatsFilters(!showStatsFilters)}
            className="flex items-center justify-between w-full px-4 py-3 bg-gradient-to-r from-orange-50 to-red-50 hover:from-orange-100 hover:to-red-100 rounded-xl transition-all duration-300"
          >
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-orange-600" />
              <h4 className="font-semibold text-gray-800">Filtros de Rendimiento</h4>
              <span className="text-xs text-gray-500">
                (Goles, Asistencias, Partidos)
              </span>
            </div>
            {showStatsFilters ? (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </button>

          {showStatsFilters && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in">
              <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200">
                <label className="block text-sm font-semibold text-orange-700 mb-2">
                  Mínimo de Goles
                </label>
                <input
                  type="number"
                  min="0"
                  value={minGoals}
                  onChange={(e) => {
                    setMinGoals(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border-2 border-orange-200 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-all duration-300"
                />
              </div>

              <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                <label className="block text-sm font-semibold text-blue-700 mb-2">
                  Mínimo de Asistencias
                </label>
                <input
                  type="number"
                  min="0"
                  value={minAssists}
                  onChange={(e) => {
                    setMinAssists(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-300"
                />
              </div>

              <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
                <label className="block text-sm font-semibold text-green-700 mb-2">
                  Mínimo de Partidos
                </label>
                <input
                  type="number"
                  min="0"
                  value={minMatchesPlayed}
                  onChange={(e) => {
                    setMinMatchesPlayed(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border-2 border-green-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModernFiltersPanel;